import { useRef, useState } from 'react';
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  type BoxProps,
} from '@chakra-ui/react';
import { SITE_NAV_STICKY_HEIGHT } from '@/components/Navbar/siteNavLayout';
import { excludeFixturesWithLiveMatches } from '@/helpers/fixtures/liveMatchDisplay';
import type { Fixture } from '@/models/fixture';
import type { gameResult } from '@/models/gameUpdate';
import type { LiveMatch } from '@/models/liveMatch';
import { FixtureListCard } from './FixtureListCard';
import { ResultListCard } from './ResultListCard';
import { MATCH_FEED_LIST_PAD_X, PROFILE_SECTION_BODY_SPACING } from './profileSectionStyles';

export function ProfileMatchFeedTabs(
  props: {
    userData: any;
    gameResults: gameResult[];
    currentMatchday: number;
    fixtures: Fixture[];
    liveMatches: LiveMatch[];
    maxW?: BoxProps['maxW'];
    resultPadX?: BoxProps['pl'];
    borderRadius?: BoxProps['borderRadius'];
    /** Desktop profile column: no sticky tabs, list scrolls inside the column. */
    embedded?: boolean;
    /** Truncate long team names to one line (standard fixture/result font size). */
    singleLineTeamNames?: boolean;
  } & Omit<BoxProps, 'children'>,
) {
  const {
    userData,
    gameResults,
    currentMatchday,
    fixtures,
    liveMatches,
    maxW,
    resultPadX,
    borderRadius,
    embedded = false,
    singleLineTeamNames = false,
    ...boxProps
  } = props;

  const upcomingFixtures = excludeFixturesWithLiveMatches(fixtures, liveMatches);
  const resultsEmpty = gameResults.length === 0;
  const fixturesEmpty = upcomingFixtures.length === 0;
  const stickyBarBg = useColorModeValue('white', 'bgBlue');
  const embeddedCardBg = useColorModeValue('gray.50', 'gray.700');
  const listPadX = embedded ? MATCH_FEED_LIST_PAD_X : (resultPadX ?? 0);
  const [tabIndex, setTabIndex] = useState(0);
  const feedTopRef = useRef<HTMLDivElement>(null);

  function handleTabChange(index: number) {
    setTabIndex(index);
    if (embedded) return;
    requestAnimationFrame(() => {
      feedTopRef.current?.scrollIntoView({ block: 'start' });
    });
  }

  const tabList = (
    <TabList w='full' flexShrink={0}>
      <Tab flex={1} justifyContent='center' fontSize={{ base: 'sm', md: 'md' }}>
        Results
      </Tab>
      <Tab flex={1} justifyContent='center' fontSize={{ base: 'sm', md: 'md' }}>
        Fixtures
      </Tab>
    </TabList>
  );

  const tabPanels = (
    <TabPanels flex={embedded ? 1 : undefined} minH={embedded ? 0 : undefined} display={embedded ? 'flex' : undefined} flexDirection={embedded ? 'column' : undefined}>
      <TabPanel
        px={0}
        pt={PROFILE_SECTION_BODY_SPACING}
        pb={0}
        flex={embedded ? 1 : undefined}
        minH={embedded ? 0 : undefined}
        display={embedded ? 'flex' : undefined}
        flexDirection={embedded ? 'column' : undefined}
      >
        {resultsEmpty ? (
          <Text fontSize='sm' color='gray.500' px={listPadX} textAlign='center'>
            No results yet.
          </Text>
        ) : (
          <ResultListCard
            userData={userData}
            gameResults={gameResults}
            currentMatchday={currentMatchday}
            maxW={maxW ?? '100%'}
            listPadX={listPadX}
            borderRadius={borderRadius}
            scrollContained={embedded}
            singleLineTeamNames={singleLineTeamNames}
            hideHeading
            w='full'
            flex={embedded ? 1 : undefined}
            minH={embedded ? 0 : undefined}
            h={embedded ? 'full' : undefined}
          />
        )}
      </TabPanel>
      <TabPanel
        px={0}
        pt={PROFILE_SECTION_BODY_SPACING}
        pb={0}
        flex={embedded ? 1 : undefined}
        minH={embedded ? 0 : undefined}
        display={embedded ? 'flex' : undefined}
        flexDirection={embedded ? 'column' : undefined}
      >
        {fixturesEmpty ? (
          <Text fontSize='sm' color='gray.500' px={listPadX} textAlign='center'>
            No upcoming fixtures.
          </Text>
        ) : (
          <FixtureListCard
            fixtures={fixtures}
            liveMatches={liveMatches}
            showLiveInline={false}
            maxW={maxW ?? '100%'}
            listPadX={listPadX}
            borderRadius={borderRadius}
            scrollContained={embedded}
            singleLineTeamNames={singleLineTeamNames}
            hideHeading
            w='full'
            flex={embedded ? 1 : undefined}
            minH={embedded ? 0 : undefined}
            h={embedded ? 'full' : undefined}
          />
        )}
      </TabPanel>
    </TabPanels>
  );

  if (embedded) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        flex={1}
        minH={0}
        w='full'
        bg={embeddedCardBg}
        borderRadius={0}
        px={3}
        py={3}
        {...boxProps}
      >
        <Tabs
          index={tabIndex}
          onChange={handleTabChange}
          variant='line'
          colorScheme='blue'
          w='full'
          isFitted
          display='flex'
          flexDirection='column'
          flex={1}
          minH={0}
        >
          {tabList}
          {tabPanels}
        </Tabs>
      </Box>
    );
  }

  return (
    <Box ref={feedTopRef} w='full' scrollMarginTop={SITE_NAV_STICKY_HEIGHT} {...boxProps}>
      <Tabs
        index={tabIndex}
        onChange={handleTabChange}
        variant='line'
        colorScheme='blue'
        w='full'
        isFitted
      >
        <Box
          position='sticky'
          top={SITE_NAV_STICKY_HEIGHT}
          zIndex={9}
          w='full'
          py={1}
          bg={stickyBarBg}
        >
          {tabList}
        </Box>
        {tabPanels}
      </Tabs>
    </Box>
  );
}
